// src/pages/SalarySlipPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./SalarySlipPage.css";
import apiClient from "../utils/apiClient"; // ✅ global API client
import SessionHandler from "../components/SessionHandler"; // ✅ global session expiry modal

const SalarySlipPage = () => {
  const [salaryData, setSalaryData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fromMonth, setFromMonth] = useState("");
  const [toMonth, setToMonth] = useState("");
  const [reason, setReason] = useState("");
  const [months, setMonths] = useState([]);
  const [filteredToMonths, setFilteredToMonths] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // ✅ NEW

  const navigate = useNavigate();

  // ✅ Generate ONLY past 3 months (exclude current month)
  useEffect(() => {
    const currentDate = new Date();
    const pastMonths = [];

    for (let i = 1; i <= 3; i++) {
      const month = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );

      pastMonths.push({
        label: month.toLocaleString("default", {
          month: "long",
          year: "numeric",
        }),
        value: month.toISOString().slice(0, 7),
      });
    }

    setMonths(pastMonths.reverse());
  }, []);

  const handleFromMonthChange = (e) => {
    const selected = e.target.value;
    setFromMonth(selected);
    setToMonth("");
    const filtered = months.filter((m) => m.value >= selected);
    setFilteredToMonths(filtered);
  };

  const handleSendRequest = async () => {
    if (isSubmitting) return;

    setError(null);
    setSuccess(null);

    if (!fromMonth || !toMonth || !reason.trim()) {
      setError("Please fill all fields: From Month, To Month, and Reason.");
      return;
    }

    if (fromMonth > toMonth) {
      setError("From Month cannot be after To Month.");
      return;
    }

    setIsSubmitting(true); // 🔒 Disable submit

    try {
      const data = await apiClient("/salary-slip/request/", {
        method: "POST",
        body: JSON.stringify({
          from_month: fromMonth,
          to_month: toMonth,
          reason: reason.trim(),
        }),
      });

      if (!data) return;

      setSuccess("Salary slip request sent successfully.");
      setShowDialog(true);

      // 🔄 Auto refresh page after success
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to send request.");
      setIsSubmitting(false); // 🔓 Re-enable on failure
    }
  };

  return (
    <div className="salary-slip-page">
      <div className="page-header">
        <h2>Salary Slip Request</h2>
        <button
          className="status-button"
          onClick={() => navigate("/dashboard/Salary/salarystatus")}
        >
          Salary Slip Status
        </button>
      </div>

      <div className="filters-row">
        <div className="month-row">
          <label>
            From Month:
            <select value={fromMonth} onChange={handleFromMonthChange}>
              <option value="">-- Select Month --</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            To Month:
            <select
              value={toMonth}
              onChange={(e) => setToMonth(e.target.value)}
              disabled={!fromMonth || isSubmitting}
            >
              <option value="">-- Select Month --</option>
              {filteredToMonths.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label>
          Reason:
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter your reason for requesting the salary slip"
            disabled={isSubmitting}
          />
        </label>

        <button
          className="SendRequest"
          onClick={handleSendRequest}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {salaryData && (
        <div className="salary-details">
          <h3>Salary Slip Details</h3>
          <p>
            <strong>Employee Name:</strong> {salaryData.employee_name}
          </p>
          <p>
            <strong>Employee ID:</strong> {salaryData.employee_id}
          </p>
          <p>
            <strong>Net Salary:</strong> {salaryData.net_salary}
          </p>
        </div>
      )}

      {showDialog && (
        <div className="dialog-overlay">
          <div className="dialog-box">
            <p>Salary slip request submitted successfully!</p>
            <button onClick={() => setShowDialog(false)}>OK</button>
          </div>
        </div>
      )}

      {/* ✅ Global session expiry modal */}
      <SessionHandler />
    </div>
  );
};

export default SalarySlipPage;
